// Chat API client — knows about the anonymous guest_token that lives in
// localStorage, and how to send it on every request. Also exposes a
// wrapper for polling.
import { api } from "./api";

const STORAGE_KEY = "kolt.chat.session";

// {conv_id, guest_token, guest_name, guest_email, started_at}
export function getSession() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function setSession(s) {
  if (!s) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

function tokenHeaders() {
  const s = getSession();
  return s?.guest_token ? { "X-Chat-Token": s.guest_token } : {};
}

export async function fetchStatus() {
  const { data } = await api.get("/chat/status");
  return data;
}

export function getVisitorId() {
  try { return localStorage.getItem("kolt.vid") || null; } catch { return null; }
}

// Visitor-side: has a staff member opened a chat aimed at THIS browser?
export async function claimOutreach() {
  const vid = getVisitorId();
  if (!vid) return null;
  const { data } = await api.get(`/chat/outreach/${encodeURIComponent(vid)}`);
  if (!data?.pending) return null;
  const conv = data.conversation;
  setSession({
    conv_id:     conv.conv_id,
    guest_token: data.guest_token,
    guest_name:  conv.guest_name,
    guest_email: conv.guest_email,
    started_at:  conv.created_at,
  });
  return data;
}

// Staff-side: start a chat on a specific live visitor's screen.
export async function staffOutreach(visitor_id, body) {
  const { data } = await api.post("/chat/staff/outreach", { visitor_id, body });
  return data;
}

export async function startConversation({ guest_name, guest_email, first_message, page_url }) {
  const { data } = await api.post("/chat/conversations", {
    guest_name, guest_email, first_message, page_url, visitor_id: getVisitorId() || undefined,
  });
  const conv = data.conversation;
  // Persist for polling reconnect.
  setSession({
    conv_id:     conv.conv_id,
    guest_token: data.guest_token,
    guest_name:  conv.guest_name,
    guest_email: conv.guest_email,
    started_at:  conv.created_at,
  });
  return data;
}

export async function getConversation(convId) {
  const { data } = await api.get(`/chat/conversations/${convId}`, {
    headers: tokenHeaders(),
  });
  return data;
}

export async function pollMessages(convId, since) {
  const { data } = await api.get(`/chat/conversations/${convId}/messages`, {
    headers: tokenHeaders(),
    params: since ? { since } : undefined,
  });
  return data;
}

export async function sendMessage(convId, { body, files }) {
  const fd = new FormData();
  fd.append("payload", JSON.stringify({ body: body || "" }));
  for (const f of files || []) fd.append("files", f);
  // Let axios set the multipart boundary itself — don't override Content-Type.
  const { data } = await api.post(`/chat/conversations/${convId}/messages`, fd, {
    headers: tokenHeaders(),
  });
  return data.message;
}

// Signal that the customer is typing. Cheap fire-and-forget. Debounced by
// the caller to no more than once every ~4 s. The backend bumps a
// customer_typing_until timestamp that staff-side inbox polling reads.
export async function signalTyping(convId) {
  try {
    await api.post(`/chat/conversations/${convId}/typing`, null, {
      headers: tokenHeaders(),
    });
  } catch { /* silent */ }
}

// Staff-only helpers (JWT is sent via axios cookies automatically).
export async function staffList(status) {
  const { data } = await api.get("/chat/staff/conversations", {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function staffPing() {
  const { data } = await api.post("/chat/staff/ping");
  return data;
}

export async function closeConversation(convId) {
  const { data } = await api.post(`/chat/conversations/${convId}/close`);
  return data;
}

export async function reopenConversation(convId) {
  const { data } = await api.post(`/chat/conversations/${convId}/reopen`);
  return data;
}

export async function deleteConversation(convId) {
  const { data } = await api.delete(`/chat/conversations/${convId}`);
  return data;
}

export function chatAttachmentUrl(attachmentId) {
  // Staff use plain URLs — the JWT cookie authorizes. Guests should use
  // downloadAttachmentAsBlob() below so the X-Chat-Token header can travel.
  const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  return `${base}/chat/attachments/${attachmentId}`;
}

export async function downloadAttachmentAsBlob(attachmentId) {
  const resp = await api.get(`/chat/attachments/${attachmentId}`, {
    headers: tokenHeaders(),
    responseType: "blob",
  });
  return resp.data;
}
